import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Chip,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useGlobalConfig } from 'context/GlobalContext';
import { PermissionGate } from 'components/access/PermissionGate';
import { UI_PAGES } from 'framework/access/UiRegistry';
import { ANALYTICS_EVENTS } from 'framework/analytics/AnalyticsEvents';
import { useToast } from 'context/ToastContext';
import { ROUTE_TREE } from 'routes/AppRoutes';
import { PilotApis } from 'apis/pilotAPI';
import { selectPilot, selectPilotList, selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { selectConfigRegistryData, setConfigRegistryData } from 'store/dataReducers/configRegistrySlice';
import type { ProjectEnvironment } from 'types/apis/pilotInterfaces';
import type { PromotionMatrixRule, PromotionMatrixResponse, TargetEnvironment, TargetEntry } from 'types/apis/configToolsSuite';
import type { DeploymentAction } from './utils/promotionMatrix';
import studioStyles from '../environments/StudioModal.module.scss';
import { BglSelect } from 'components/common/BglSelect';
import { buildShellBundlePayload } from './utils/shellBundlePayload';
import { formatLevelDisplay } from './utils/levelDisplay';
import { safeStringify } from './utils/historyParsers';
import { findBestMatrixRule, computeEffectivePolicy, getRuleProp } from './utils/promotionMatrix';
import {
  getConfigCompareSnapshot,
  removeConfigCompareSnapshot,
  setConfigCompareSnapshot,
} from './utils/configCompareSnapshotStorage';
import { PromotionHistory } from './PromotionHistory';
import { PromotionFilter } from './PromotionFilter';

type ViewMode = 'selection' | 'compare' | 'history';

type DiffRow = {
  category: string;
  id: string;
  selectionId: string;
  key: string;
  displayName: string;
  sourceValueRaw: any;
  targetValueRaw: any;
  sourceValueDisplay: string;
  targetValueDisplay: string;
  sourceLevel: string;
  targetLevel: string;
  status: 'new' | 'modified' | 'conflict' | 'unchanged' | 'target_only';
  conflictReason: string | null;
  metadata: {
    configType?: string;
    level?: string;
    [k: string]: any;
  };
  /** Per-target value/status; order matches targetEnvironments. When present, use for display and push. */
  targets?: TargetEntry[];
};

/** selectionId -> envId -> new value (for environment configs multi-env edit). */
type NonprodNewValuesMap = Record<string, Record<string | number, string>>;

const PAGE_SIZE = 10;
const PENDING_PROMOTION_SESSION_KEY = 'pending_config_promotion';

type StudioButtonVariant = 'primary' | 'outline';
type StudioButtonSize = 'sm' | 'md';

function StudioButton({
  children,
  onClick,
  disabled,
  variant = 'primary',
  size = 'md',
  startIcon,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: StudioButtonVariant;
  size?: StudioButtonSize;
  startIcon?: React.ReactNode;
}) {
  const className =
    variant === 'primary'
      ? `${studioStyles.button} ${studioStyles.primaryButton}`
      : `${studioStyles.button} ${studioStyles.outlineButton}`;

  const style: React.CSSProperties | undefined =
    size === 'sm'
      ? { height: 32, padding: '0 12px', fontSize: 13 }
      : undefined;

  return (
    <button type="button" className={className} onClick={onClick} disabled={disabled} style={style}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        {startIcon ? <span style={{ display: 'inline-flex', alignItems: 'center' }}>{startIcon}</span> : null}
        {children}
      </span>
    </button>
  )
}

function StudioField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className={studioStyles.field}>
      <label className={studioStyles.label}>{label}</label>
      {children}
    </div>
  );
}

function getEnvLabel(env: Partial<ProjectEnvironment> | undefined): string {
  if (!env) return '';
  const name = (env as any).environment_name || env.environment_name || env.name || String(env.id ?? '');
  const stack = env.stack_name ? ` (${env.stack_name})` : '';
  return `${name}${stack}`;
}

type CompareSectionSummary = {
  totalCount?: number;
  newCount?: number;
  modifiedCount?: number;
  conflictCount?: number;
};

function parseTargetEnvironments(container: any): TargetEnvironment[] {
  const raw = container?.targetEnvironments;
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw
    .filter((e: any) => e && typeof e.id === 'number')
    .map((e: any) => ({
      id: Number(e.id),
      environmentName: String(e.environmentName ?? ''),
      type: String(e.type ?? ''),
      stackName: e.stackName != null ? String(e.stackName) : undefined,
    }));
}

function normalizeCompareResponse(raw: any): {
  comparisonId: string | null;
  cached: boolean;
  lastComparedAt: string | null;
  targetEnvironments: TargetEnvironment[];
  sections: Record<string, DiffRow[]>;
  summaries: Record<string, CompareSectionSummary>;
} {
  const container = raw?.data ?? raw; // support {data: ...} or raw directly
  // Backend variants seen: comparisonId (uuid) OR eventId (number) OR id
  const comparisonId =
    container?.comparisonId ? String(container.comparisonId) :
    (typeof container?.eventId !== 'undefined' && container?.eventId !== null) ? String(container.eventId) :
    (typeof container?.id !== 'undefined' && container?.id !== null) ? String(container.id) :
    null;
  const cached = Boolean(container?.cached);
  const lastComparedAt = container?.lastComparedAt ? String(container.lastComparedAt) : null;
  const targetEnvironments = parseTargetEnvironments(container);

  // Preferred: { categories: { configs: { items: [...], totalCount, ... }, ... } }
  const categories = container?.categories;
  if (categories && typeof categories === 'object' && !Array.isArray(categories)) {
    const sections: Record<string, DiffRow[]> = {};
    const summaries: Record<string, CompareSectionSummary> = {};

    Object.entries(categories).forEach(([k, v]) => {
      const items = Array.isArray((v as any)?.items) ? (v as any).items : [];
      sections[k] = items.map((it: any, idx: number) => normalizeItem(it, idx, k));
      summaries[k] = {
        totalCount: typeof (v as any)?.totalCount === 'number' ? (v as any).totalCount : undefined,
        newCount: typeof (v as any)?.newCount === 'number' ? (v as any).newCount : undefined,
        modifiedCount: typeof (v as any)?.modifiedCount === 'number' ? (v as any).modifiedCount : undefined,
        conflictCount: typeof (v as any)?.conflictCount === 'number' ? (v as any).conflictCount : undefined,
      };
    });

    const rawSections = Object.keys(sections).length ? sections : { configs: [] };
    const { sections: finalSections, summaries: finalSummaries } = splitEnvVarSections(rawSections, summaries);
    return { comparisonId, cached, lastComparedAt, targetEnvironments, sections: finalSections, summaries: finalSummaries };
  }

  // Back-compat fallbacks (older shapes)
  if (Array.isArray(container)) {
    const sections = { configs: container.map((it, idx) => normalizeItem(it, idx, 'configs')) };
    const { sections: finalSections, summaries: finalSummaries } = splitEnvVarSections(sections, {});
    return { comparisonId, cached, lastComparedAt, targetEnvironments: [], sections: finalSections, summaries: finalSummaries };
  }

  const sectionsCandidate = container && typeof container === 'object' && !Array.isArray(container) ? container : null;
  if (sectionsCandidate) {
    const entries = Object.entries(sectionsCandidate).filter(([, v]) => Array.isArray(v));
    if (entries.length > 0) {
      const sections = Object.fromEntries(entries.map(([k, v]) => [k, (v as any[]).map((it, idx) => normalizeItem(it, idx, k))]));
      const { sections: finalSections, summaries: finalSummaries } = splitEnvVarSections(sections, {});
      return { comparisonId, cached, lastComparedAt, targetEnvironments: [], sections: finalSections, summaries: finalSummaries };
    }
  }

  const nested = container?.diff ?? container?.result ?? null;
  if (nested && typeof nested === 'object') {
    const entries = Object.entries(nested).filter(([, v]) => Array.isArray(v));
    if (entries.length > 0) {
      const sections = Object.fromEntries(entries.map(([k, v]) => [k, (v as any[]).map((it, idx) => normalizeItem(it, idx, k))]));
      const { sections: finalSections, summaries: finalSummaries } = splitEnvVarSections(sections, {});
      return { comparisonId, cached, lastComparedAt, targetEnvironments: [], sections: finalSections, summaries: finalSummaries };
    }
  }

  return { comparisonId, cached, lastComparedAt, targetEnvironments: [], sections: { configs: [] }, summaries: {} };
}

/** Splits out items with metadata.isEnvVar === true into Environment Configs / Environment Strings tabs. */
function splitEnvVarSections(
  sections: Record<string, DiffRow[]>,
  summaries: Record<string, CompareSectionSummary>
): { sections: Record<string, DiffRow[]>; summaries: Record<string, CompareSectionSummary> } {
  const environmentConfigs: DiffRow[] = [];
  const environmentStrings: DiffRow[] = [];
  const newSections: Record<string, DiffRow[]> = {};
  const newSummaries: Record<string, CompareSectionSummary> = {};

  for (const [cat, items] of Object.entries(sections)) {
    const isStringCategory = cat.toLowerCase().includes('string');
    const kept: DiffRow[] = [];
    for (const row of items) {
      if (row.metadata?.isEnvVar === true) {
        if (isStringCategory) environmentStrings.push(row);
        else environmentConfigs.push(row);
      } else {
        kept.push(row);
      }
    }
    newSections[cat] = kept;
    newSummaries[cat] = summaries[cat] || {};
  }
  if (environmentConfigs.length > 0) {
    newSections['environmentConfigs'] = environmentConfigs;
    newSummaries['environmentConfigs'] = {
      totalCount: environmentConfigs.length,
      newCount: environmentConfigs.filter((r) => r.status === 'new').length,
      modifiedCount: environmentConfigs.filter((r) => r.status === 'modified').length,
      conflictCount: environmentConfigs.filter((r) => r.status === 'conflict').length,
    };
  }
  if (environmentStrings.length > 0) {
    newSections['environmentStrings'] = environmentStrings;
    newSummaries['environmentStrings'] = {
      totalCount: environmentStrings.length,
      newCount: environmentStrings.filter((r) => r.status === 'new').length,
      modifiedCount: environmentStrings.filter((r) => r.status === 'modified').length,
      conflictCount: environmentStrings.filter((r) => r.status === 'conflict').length,
    };
  }
  return { sections: newSections, summaries: newSummaries };
}

function parseTargetEntry(entry: any): TargetEntry | null {
  if (!entry || typeof entry.envId === 'undefined') return null;
  const envId = Number(entry.envId);
  if (Number.isNaN(envId)) return null;
  const rawStatus = String(entry?.status ?? '').toLowerCase();
  const status =
    rawStatus === 'new' || rawStatus.includes('new') ? 'new'
    : rawStatus === 'modified' || rawStatus.includes('modified') ? 'modified'
    : rawStatus === 'conflict' || rawStatus.includes('conflict') ? 'conflict'
    : rawStatus === 'target_only' || rawStatus.includes('target_only') ? 'target_only'
    : 'unchanged';
  return {
    envId,
    environmentName: String(entry.environmentName ?? ''),
    type: String(entry.type ?? ''),
    targetValue: entry.targetValue ?? null,
    status,
    dbValue: entry.dbValue,
  };
}

function normalizeItem(it: any, idx: number, category: string): DiffRow {
  const key = String(it?.key ?? `item_${idx}`);
  const displayName = String(it?.displayName ?? key);
  // Do not default to '' — preserve null/undefined so we can show "Not Set" when key is not in source (API sends null).
  const sourceValueRaw = it?.sourceValue;
  // Preserve full metadata from API as-is. StringResources have metadata.tags (comma-separated string); configs do not have tags.
  const rawMeta = it?.metadata && typeof it.metadata === 'object' ? it.metadata : {};
  const metadata = {
    ...rawMeta,
    ...(it?.tags !== undefined && it?.tags !== null ? { tags: it.tags } : {}),
  } as DiffRow['metadata'];
  const levelRaw = String(metadata?.level ?? 'N/A').trim().toLowerCase();
  const displayPrefix = levelRaw === 'cluster' ? 'CLUSTER' : 'PILOT';
  const rawLevelDisplay = metadata?.levelDisplay;
  const levelDisplayPart = rawLevelDisplay != null && String(rawLevelDisplay).trim() !== '' ? String(rawLevelDisplay).trim() : '';
  const levelDisplayPartLower = levelDisplayPart.toLowerCase();
  const isRedundant =
    levelDisplayPartLower === displayPrefix.toLowerCase() || levelDisplayPartLower === levelRaw;
  const levelDisplay = levelDisplayPart && !isRedundant ? `${displayPrefix}.${levelDisplayPart}` : displayPrefix;
  const sourceLevel = levelDisplay;

  // Prefer item.targets (new shape); fallback to item.targetValue / item.status
  const targetsRaw = Array.isArray(it?.targets) ? it.targets : [];
  const targets: TargetEntry[] = targetsRaw
    .map((e: any) => parseTargetEntry(e))
    .filter((entry: TargetEntry | null): entry is TargetEntry => entry != null);
  const firstTarget = targets.length > 0 ? targets[0] : null;

  const rawStatus = firstTarget
    ? firstTarget.status
    : String(it?.status ?? '').toLowerCase();
  // Not Set for source when key is not defined in source: API sends null/undefined.
  // Status 'new' or 'target_only' alone doesn't mean source is missing - check actual value first.
  const sourceValueDisplay =
    sourceValueRaw === null || typeof sourceValueRaw === 'undefined'
      ? 'Not Set'
      : safeStringify(sourceValueRaw);

  const targetValueRaw = firstTarget ? firstTarget.targetValue : (it?.targetValue ?? null);
  // Not Set only when key is not defined in that env (null/undefined); empty value = empty text.
  const targetValueDisplay =
    targetValueRaw === null || typeof targetValueRaw === 'undefined'
      ? 'Not Set'
      : safeStringify(targetValueRaw);
  const status: DiffRow['status'] =
    rawStatus === 'new' || rawStatus.includes('new')
      ? 'new'
      : rawStatus === 'modified' || rawStatus.includes('modified')
        ? 'modified'
        : rawStatus === 'conflict' || rawStatus.includes('conflict')
          ? 'conflict'
          : rawStatus === 'target_only' || rawStatus.includes('target_only')
            ? 'target_only'
            : 'unchanged';
  const conflictReason = it?.conflictReason ?? null;
  const targetLevel = targetValueRaw === null || typeof targetValueRaw === 'undefined' ? 'Not Set' : levelDisplay;
  const id = it?.id != null ? String(it.id) : `${key}__${idx}`;
  const selectionId = `${category}::${id || key}`;

  return {
    category,
    id,
    selectionId,
    key,
    displayName,
    sourceValueRaw,
    targetValueRaw,
    sourceValueDisplay,
    targetValueDisplay,
    sourceLevel,
    targetLevel,
    status,
    conflictReason: conflictReason != null ? String(conflictReason) : null,
    metadata,
    ...(targets.length > 0 ? { targets } : {}),
  };
}

type RegistryEntry = {
  configDocumentation: string;
  possibleValues: any;
  component: string;
  configType: string;
  displayName: string;
};

export const ConfigComparePage = () => {
  const { rc } = useGlobalConfig();
  const toast = useToast();
  const dispatch = useDispatch();
  const location = useLocation();
  const theme = useTheme();
  const palette = theme.palette;
  const hasRestoredSnapshotRef = useRef(false);
  const hasProcessedRestoreRef = useRef(false);
  const [pendingRestoreFromPayload, setPendingRestoreFromPayload] = useState<{
    projectId: string;
    sourceEnvId: number;
    targetEnvId: number;
    targetEnvironments: TargetEnvironment[];
    selectedIds: string[];
  } | null>(null);

  const pilotList = useSelector(selectPilotList) as any[];
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const selectedPilot = useMemo(
    () => pilotList.find((p) => p?.projectId === selectedPilotId),
    [pilotList, selectedPilotId]
  );
  const configRegistryData = useSelector(selectConfigRegistryData);

  const rcRef = useRef(rc);
  useEffect(() => {
    rcRef.current = rc;
  }, [rc]);

  // Track page open on mount
  useEffect(() => {
    rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.CONFIG_COMPARE_OPENED)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [viewMode, setViewMode] = useState<ViewMode>('selection');
  const [isLoadingEnvs, setIsLoadingEnvs] = useState(false);
  const [isComparing, setIsComparing] = useState(false);

  const [environments, setEnvironments] = useState<ProjectEnvironment[]>([]);
  const [sourceEnvId, setSourceEnvId] = useState<string>('');
  const [targetEnvId, setTargetEnvId] = useState<string>('');

  const [query, setQuery] = useState('');
  const [onlyDifferences, setOnlyDifferences] = useState(true);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  type SortKeyType = 'configKey' | 'configType' | 'level' | 'status';
  const [sortKey, setSortKey] = useState<SortKeyType>('configKey');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filterStatus, setFilterStatus] = useState<string[]>([]);
  const [filterLevel, setFilterLevel] = useState<string[]>([]);
  const [filterComponent, setFilterComponent] = useState<string[]>([]);
  const [filterConfigType, setFilterConfigType] = useState<string[]>([]);

  const fetchAssociatedEnvironments = useCallback(async (projectId: string) => {
    setIsLoadingEnvs(true);
    try {
      const resp = await PilotApis.getAssociatedEnvironments(rcRef.current, projectId);
      if (resp.error) {
        toast.error(resp.error.message || 'Failed to fetch associated environments');
        setEnvironments([]);
        return;
      }
      setEnvironments(resp.data?.data || []);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to fetch associated environments');
      setEnvironments([]);
    } finally {
      setIsLoadingEnvs(false);
    }
  }, [toast]);

  // Load environments for the selected project.
  useEffect(() => {
    if (selectedPilot?.projectId) {
      fetchAssociatedEnvironments(selectedPilot.projectId);
    } else {
      setEnvironments([]);
    }
  }, [selectedPilot?.projectId, fetchAssociatedEnvironments]);

  const lastResetKeyRef = useRef<string>('');

  // Default source to project's primary environment, if present.
  // IMPORTANT: do not reset the compare view just because `environments` refreshed.
  // Skip reset when we've just restored from Review Back (preserve compare + selections).
  useEffect(() => {
    if (hasRestoredSnapshotRef.current) {
      // Delay resetting the flag to ensure all state updates from restore have completed
      setTimeout(() => {
        hasRestoredSnapshotRef.current = false;
      }, 100);
      return;
    }
    const projectId = selectedPilot?.projectId ? String(selectedPilot.projectId) : '';
    const primary = selectedPilot?.environment_id ? String(selectedPilot.environment_id) : '';
    const resetKey = `${projectId}::${primary}`;

    // Always keep source aligned to primary if available.
    if (primary) {
      setSourceEnvId(primary);
    }

    // Only reset selection/compare state when project/source env changes.
    if (lastResetKeyRef.current === resetKey) return;
    lastResetKeyRef.current = resetKey;

    setTargetEnvId('');
    setViewMode('selection');
    setSelectedIds(new Set());
    setPage(1);
  }, [selectedPilot?.projectId, selectedPilot?.environment_id]);

  // Fallback: if primary env isn't available, choose first env once loaded (but do NOT reset viewMode).
  useEffect(() => {
    const hasPrimary = !!selectedPilot?.environment_id;
    if (hasPrimary) return;
    if (sourceEnvId) return;
    if (environments.length === 0) return;

    setSourceEnvId(String(environments[0].id));
  }, [selectedPilot?.environment_id, environments, sourceEnvId]);

  const sourceEnv = useMemo(
    () => environments.find((e) => String(e.id) === String(sourceEnvId)),
    [environments, sourceEnvId]
  );
  const targetEnv = useMemo(
    () => environments.find((e) => String(e.id) === String(targetEnvId)),
    [environments, targetEnvId]
  );

  const availableTargetEnvs = useMemo(
    () => environments.filter((e) => String(e.id) !== String(sourceEnvId)),
    [environments, sourceEnvId]
  );

  const sourceEnvOptions = useMemo(
    () =>
      environments.map((env) => ({
        value: String(env.id),
        label: `${getEnvLabel(env)}${env.type ? ` — ${env.type}` : ''}`,
      })),
    [environments]
  );

  const targetEnvOptions = useMemo(
    () =>
      availableTargetEnvs.map((env) => ({
        value: String(env.id),
        label: `${getEnvLabel(env)}${env.type ? ` — ${env.type}` : ''}`,
      })),
    [availableTargetEnvs]
  );

  const [diffRows, setDiffRows] = useState<DiffRow[]>([]);
  const [sections, setSections] = useState<Record<string, DiffRow[]>>({ configs: [] });
  const [sectionSummaries, setSectionSummaries] = useState<Record<string, CompareSectionSummary>>({});
  const [compareTargetEnvironments, setCompareTargetEnvironments] = useState<TargetEnvironment[]>([]);
  const [sectionKey, setSectionKey] = useState<string>('configs');
  /** Edits for Environment Configs / Environment Strings: selectionId -> target value (string). */
  const [envVarEdits, setEnvVarEdits] = useState<Record<string, string>>({});
  /** Per-selection, per-target-environment "New value" for environment configs (selectionId -> envId -> value). */
  const [nonprodNewValues, setNonprodNewValues] = useState<NonprodNewValuesMap>({});
  const [compareCached, setCompareCached] = useState<boolean>(false);
  const [compareLastComparedAt, setCompareLastComparedAt] = useState<string | null>(null);
  const [openEnvConfigsAttentionModal, setOpenEnvConfigsAttentionModal] = useState(false);
  const [isNavigatingToReview, setIsNavigatingToReview] = useState(false);

  const [promotionMatrix, setPromotionMatrix] = useState<PromotionMatrixResponse | null>(null);

  const [isDeploying, setIsDeploying] = useState(false);
  const [syncId, setSyncId] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<any | null>(null);
  const [isLoadingSyncStatus, setIsLoadingSyncStatus] = useState(false);
  const [shellBundle, setShellBundle] = useState<any | null>(null);
  const [isCreatingZipBundle, setIsCreatingZipBundle] = useState(false);

  const loadPromotionMatrix = useCallback(async () => {
    try {
      const resp = await PilotApis.getPromotionMatrix(rcRef.current);
      if (resp.error) {
        const msg =
          typeof resp.error === 'string'
            ? resp.error
            : (resp.error.message || (resp.error.code ? `${resp.error.code}` : '') || 'Failed to load promotion matrix');
        const suffix = typeof resp.error === 'object' && resp.error?.code ? ` (${resp.error.code})` : '';
        const finalMsg = `${msg}${suffix}`.trim();
        toast.error(finalMsg);
        return;
      }
      setPromotionMatrix(resp.data as PromotionMatrixResponse);
    } finally {}
  }, [toast]);

  // Load matrix once (UI will re-match rules on env changes).
  useEffect(() => {
    loadPromotionMatrix();
  }, [loadPromotionMatrix]);

  const matchedMatrixRule = useMemo(() => {
    const rules = (promotionMatrix as any)?.rules as PromotionMatrixRule[] | undefined;
    if (!rules || rules.length === 0) return null;
    return findBestMatrixRule(rules, sourceEnv, targetEnv);
  }, [promotionMatrix, sourceEnv, targetEnv]);

  const handleCompare = async () => {
    if (!sourceEnv || !targetEnv) return;
    rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.CONFIG_COMPARE_MERGE_REQUESTED, {
      source_env: sourceEnv.type ?? '',
      target_env: targetEnv.type ?? '',
    });
    setIsComparing(true);
    setSelectedIds(new Set());
    setPage(1);
    try {
      const projectId = selectedPilot?.projectId;
      if (!projectId) {
        toast.error('Project ID missing. Select a project first.');
        return;
      }

      const resp = await PilotApis.compareConfigTools(rcRef.current, {
        projectId,
        sourceEnvId: Number(sourceEnv.id),
        targetEnvId: Number(targetEnv.id),
      });

      if (resp.error) {
        const msg =
          typeof resp.error === 'string'
            ? resp.error
            : (resp.error.message || (resp.error.code ? `${resp.error.code}` : '') || 'Compare failed');
        const suffix = typeof resp.error === 'object' && resp.error?.code ? ` (${resp.error.code})` : '';
        toast.error(`${msg}${suffix}`.trim());
        return;
      }

      const normalized = normalizeCompareResponse(resp.data);
      setCompareCached(normalized.cached);
      setCompareLastComparedAt(normalized.lastComparedAt);
      setCompareTargetEnvironments(normalized.targetEnvironments);
      setSections(normalized.sections);
      setSectionSummaries(normalized.summaries);
      setEnvVarEdits({});
      
      // Initialize nonprodNewValues with dbValue from targets for environmentConfigs and environmentStrings
      const initialNonprodNewValues: Record<string, Record<number, string>> = {};
      Object.values(normalized.sections).forEach(sectionItems => {
        sectionItems.forEach(item => {
          if (item.targets && Array.isArray(item.targets)) {
            item.targets.forEach(target => {
              if (target.dbValue !== undefined && target.dbValue !== null && target.dbValue !== '') {
                if (!initialNonprodNewValues[item.selectionId]) {
                  initialNonprodNewValues[item.selectionId] = {};
                }
                initialNonprodNewValues[item.selectionId][target.envId] = String(target.dbValue);
              }
            });
          }
        });
      });
      setNonprodNewValues(initialNonprodNewValues);
      const firstKey = Object.keys(normalized.sections)[0] || 'configs';
      setSectionKey(firstKey);
      const rows = normalized.sections[firstKey] || [];
      setDiffRows(rows);
      setViewMode('compare');

      // Report conflicts surfaced by the compare result, if any.
      const conflictCount = Object.values(normalized.sections).reduce(
        (acc, sectionItems) => acc + sectionItems.filter((r) => r.status === 'conflict').length,
        0
      );
      if (conflictCount > 0) {
        rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.CONFIG_COMPARE_MERGE_CONFLICT_DETECTED, {
          conflict_count: conflictCount,
        });
      }

      toast.success('Comparison complete.');
    } finally {
      setIsComparing(false);
    }
  };

  // When returning from Review via Back: restore last compare result and selections from IndexedDB snapshot.
  // useLayoutEffect runs BEFORE useEffect, so we set refs immediately so reset effect skips; async restore follows.
  const isMountedRef = useRef(true);
  useLayoutEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  useLayoutEffect(() => {
    const state = location.state as { restoreCompare?: boolean } | undefined;
    if (!state?.restoreCompare) {
      hasProcessedRestoreRef.current = false;
      return;
    }
    if (hasProcessedRestoreRef.current) return;

    hasProcessedRestoreRef.current = true;
    hasRestoredSnapshotRef.current = true;

    getConfigCompareSnapshot<
      {
        projectId: string;
        sourceEnvId: string;
        targetEnvId: string;
        targetEnvironments?: TargetEnvironment[];
        sections: Record<string, DiffRow[]>;
        sectionSummaries: Record<string, CompareSectionSummary>;
        sectionKey: string;
        selectedIds: string[];
        page: number;
        rowsPerPage: number;
        query: string;
        onlyDifferences: boolean;
        sortKey: SortKeyType;
        sortOrder: 'asc' | 'desc';
        filterStatus: string[];
        filterLevel: string[];
        filterComponent: string[];
        filterConfigType: string[];
        envVarEdits?: Record<string, string>;
        nonprodNewValues?: NonprodNewValuesMap;
      }
    >().then((snapshot) => {
      if (!isMountedRef.current) return;
      if (snapshot) {
        lastResetKeyRef.current = `${snapshot.projectId}::${snapshot.sourceEnvId}`;
        setSourceEnvId(snapshot.sourceEnvId);
        setTargetEnvId(snapshot.targetEnvId);
        setCompareTargetEnvironments(Array.isArray(snapshot.targetEnvironments) ? snapshot.targetEnvironments : []);
        setSections(snapshot.sections || { configs: [] });
        setSectionSummaries(snapshot.sectionSummaries || {});
        setSectionKey(snapshot.sectionKey || 'configs');
        setSelectedIds(new Set(snapshot.selectedIds || []));
        setPage(snapshot.page ?? 1);
        setRowsPerPage(snapshot.rowsPerPage ?? 10);
        setQuery(snapshot.query ?? '');
        setOnlyDifferences(snapshot.onlyDifferences ?? true);
        setSortKey(snapshot.sortKey ?? 'configKey');
        setSortOrder(snapshot.sortOrder ?? 'asc');
        setFilterStatus(Array.isArray(snapshot.filterStatus) ? snapshot.filterStatus : []);
        setFilterLevel(Array.isArray(snapshot.filterLevel) ? snapshot.filterLevel : []);
        setFilterComponent(Array.isArray(snapshot.filterComponent) ? snapshot.filterComponent : []);
        setFilterConfigType(Array.isArray(snapshot.filterConfigType) ? snapshot.filterConfigType : []);
        const rows = (snapshot.sections || {})[snapshot.sectionKey || 'configs'] || [];
        setDiffRows(rows);
        setViewMode('compare');
        setEnvVarEdits(snapshot.envVarEdits || {});
        setNonprodNewValues(snapshot.nonprodNewValues || {});
        removeConfigCompareSnapshot();
        return;
      }

      // Fallback: payload + re-fetch when IndexedDB fails or returns null (e.g. private browsing, SSR)
      if (!isMountedRef.current) return;
      const payloadRaw = sessionStorage.getItem(PENDING_PROMOTION_SESSION_KEY);
      if (payloadRaw) {
        try {
          const payload = JSON.parse(payloadRaw) as {
            projectId: string;
            sourceEnvId: number;
            targetEnvId: number;
            targetEnvironments?: TargetEnvironment[];
            items?: Array<{ category?: string; key?: string; selectionId?: string; metadata?: { selectionId?: string } }>;
          };
          const projectId = payload.projectId;
          const sourceEnvId = payload.sourceEnvId;
          const targetEnvId = payload.targetEnvId;
          if (projectId && sourceEnvId != null && targetEnvId != null) {
            const ids = new Set<string>(
              (payload.items || [])
                .map((i) => i.selectionId ?? i.metadata?.selectionId ?? (i.category && i.key ? `${i.category}::${i.key}` : ''))
                .filter(Boolean)
            );
            lastResetKeyRef.current = `${projectId}::${sourceEnvId}`;
            dispatch(selectPilot({ projectId }));
            setSourceEnvId(String(sourceEnvId));
            setTargetEnvId(String(targetEnvId));
            setCompareTargetEnvironments(Array.isArray(payload.targetEnvironments) ? payload.targetEnvironments : []);
            setSelectedIds(ids);
            setViewMode('compare');
            setPendingRestoreFromPayload({
              projectId,
              sourceEnvId,
              targetEnvId,
              targetEnvironments: Array.isArray(payload.targetEnvironments) ? payload.targetEnvironments : [],
              selectedIds: Array.from(ids),
            });
            sessionStorage.removeItem(PENDING_PROMOTION_SESSION_KEY);
            return;
          }
        } catch {
          sessionStorage.removeItem(PENDING_PROMOTION_SESSION_KEY);
        }
      }

      if (!isMountedRef.current) return;
      hasProcessedRestoreRef.current = false;
      hasRestoredSnapshotRef.current = false;
      rc.uiRouter.navigate(ROUTE_TREE.CONFIG_TOOLS.CONFIG_COMPARE, { replace: true });
    });
  }, [location.state, rc.uiRouter, dispatch]);

  // When restore falls back to payload (snapshot missing), re-fetch compare once envs are ready.
  useEffect(() => {
    if (!pendingRestoreFromPayload || !sourceEnv || !targetEnv) return;
    const { projectId, sourceEnvId, targetEnvId, selectedIds: ids } = pendingRestoreFromPayload;
    if (String(sourceEnv.id) !== String(sourceEnvId) || String(targetEnv.id) !== String(targetEnvId)) return;
    setPendingRestoreFromPayload(null);
    setIsComparing(true);
    (async () => {
      try {
        const resp = await PilotApis.compareConfigTools(rcRef.current, {
          projectId,
          sourceEnvId,
          targetEnvId,
        });
        if (resp.error) {
          const msg =
            typeof resp.error === 'string'
              ? resp.error
              : (resp.error.message || (resp.error.code ? `${resp.error.code}` : '') || 'Compare failed');
          const suffix = typeof resp.error === 'object' && resp.error?.code ? ` (${resp.error.code})` : '';
          toast.error(`${msg}${suffix}`.trim());
          return;
        }
        const normalized = normalizeCompareResponse(resp.data);
        setCompareCached(normalized.cached);
        setCompareLastComparedAt(normalized.lastComparedAt);
        setCompareTargetEnvironments(normalized.targetEnvironments);
        setSections(normalized.sections);
        setSectionSummaries(normalized.summaries);
        setEnvVarEdits({});
        const initialNonprodNewValues: Record<string, Record<number, string>> = {};
        Object.values(normalized.sections).forEach((sectionItems) => {
          sectionItems.forEach((item) => {
            if (item.targets && Array.isArray(item.targets)) {
              item.targets.forEach((target) => {
                if (target.dbValue !== undefined && target.dbValue !== null && target.dbValue !== '') {
                  if (!initialNonprodNewValues[item.selectionId]) {
                    initialNonprodNewValues[item.selectionId] = {};
                  }
                  initialNonprodNewValues[item.selectionId][target.envId] = String(target.dbValue);
                }
              });
            }
          });
        });
        setNonprodNewValues(initialNonprodNewValues);
        const firstKey = Object.keys(normalized.sections)[0] || 'configs';
        setSectionKey(firstKey);
        const rows = normalized.sections[firstKey] || [];
        setDiffRows(rows);
        setSelectedIds(new Set(ids));
        setViewMode('compare');
        toast.success('Comparison restored.');
      } finally {
        setIsComparing(false);
      }
    })();
  }, [pendingRestoreFromPayload, sourceEnv, targetEnv, toast]);

  // Update current diff list when tab/section changes (keep selection across tabs).
  useEffect(() => {
    setDiffRows(sections[sectionKey] || []);
    // Don't reset page when restoring from Review - we want to preserve the restored page
    if (!hasRestoredSnapshotRef.current) {
      setPage(1);
    }
  }, [sectionKey, sections]);

  // Lazy-load config registry when compare view has data (for Config Key info tooltips).
  useEffect(() => {
    if (viewMode !== 'compare') return;
    const hasData = Object.values(sections).some((arr) => arr.length > 0);
    if (!hasData || configRegistryData.length > 0) return;
    let cancelled = false;
    (async () => {
      const resp = await PilotApis.getConfigRegistry(rcRef.current);
      if (cancelled) return;
      if (resp.success && resp.data && Array.isArray(resp.data)) {
        dispatch(setConfigRegistryData(resp.data));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [viewMode, sections, configRegistryData.length, dispatch]);

  const registryMap = useMemo(() => {
    const map = new Map<string, RegistryEntry>();
    if (!Array.isArray(configRegistryData)) return map;
    configRegistryData.forEach((typeBlock) => {
      const configType = typeBlock.configType ?? '';
      (typeBlock.configs ?? []).forEach((c: any) => {
        const key = c.configKey ?? c.config_key ?? '';
        if (!key) return;
        const existing = map.get(key);
        if (!existing) {
          map.set(key, {
            configDocumentation: c.configDocumentation ?? c.config_documentation ?? '',
            possibleValues: c.possibleValues ?? c.possible_values ?? '',
            component: c.Module ?? c.component ?? '',
            configType,
            displayName: c.displayName ?? c.display_name ?? '',
          });
        }
      });
    });
    return map;
  }, [configRegistryData]);

  const filterOptions = useMemo(() => {
    const statuses = new Set<string>();
    const levels = new Set<string>();
    const components = new Set<string>();
    const configTypes = new Set<string>();
    diffRows.forEach((r) => {
      statuses.add(r.status);
      levels.add(r.sourceLevel || '');
      const comp = r.metadata?.component ?? r.metadata?.componentName ?? '';
      if (comp) components.add(String(comp));
      const ct = r.metadata?.configType ?? r.category ?? '';
      if (ct) configTypes.add(String(ct));
    });
    return {
      statuses: Array.from(statuses).filter(Boolean).sort(),
      levels: Array.from(levels).filter(Boolean).sort(),
      components: Array.from(components).sort(),
      configTypes: Array.from(configTypes).sort(),
    };
  }, [diffRows]);

  const applyFiltersToRows = useCallback(
    (rows: DiffRow[]) => {
      const q = query.trim().toLowerCase();
      return rows.filter((r) => {
        if (onlyDifferences) {
          if (r.status === 'unchanged') return false;
        }
        if (filterStatus.length > 0 && !filterStatus.includes(r.status)) return false;
        if (filterLevel.length > 0 && !filterLevel.includes(r.sourceLevel ?? '')) return false;
        const comp = r.metadata?.component ?? r.metadata?.componentName ?? '';
        if (filterComponent.length > 0 && !filterComponent.includes(String(comp))) return false;
        const ct = r.metadata?.configType ?? r.category ?? '';
        if (filterConfigType.length > 0 && !filterConfigType.includes(String(ct))) return false;
        if (!q) return true;
        return (
          r.key.toLowerCase().includes(q) ||
          r.displayName.toLowerCase().includes(q) ||
          r.sourceValueDisplay.toLowerCase().includes(q) ||
          r.targetValueDisplay.toLowerCase().includes(q) ||
          String(r.metadata?.component ?? '').toLowerCase().includes(q) ||
          String(r.metadata?.configType ?? '').toLowerCase().includes(q)
        );
      });
    },
    [onlyDifferences, query, filterStatus, filterLevel, filterComponent, filterConfigType]
  );

  const filteredRows = useMemo(
    () => applyFiltersToRows(diffRows),
    [diffRows, applyFiltersToRows]
  );

  /** Filtered counts per section for tab labels (honors onlyDifferences + search + filters). */
  const filteredSectionCounts = useMemo(() => {
    const counts: Record<string, { total: number; conflicts: number }> = {};
    Object.entries(sections).forEach(([k, rows]) => {
      const filtered = applyFiltersToRows(rows);
      counts[k] = {
        total: filtered.length,
        conflicts: filtered.filter((r) => r.status === 'conflict').length,
      };
    });
    return counts;
  }, [sections, applyFiltersToRows]);

  const sortedRows = useMemo(() => {
    const key = sortKey;
    const order = sortOrder;
    return [...filteredRows].sort((a, b) => {
      let aVal: string;
      let bVal: string;
      if (key === 'configKey') {
        aVal = (a.metadata?.configKey ?? a.key).toLowerCase();
        bVal = (b.metadata?.configKey ?? b.key).toLowerCase();
      } else if (key === 'configType') {
        aVal = (a.metadata?.configType ?? '—').toLowerCase();
        bVal = (b.metadata?.configType ?? '—').toLowerCase();
      } else if (key === 'level') {
        aVal = (a.sourceLevel ?? '').toLowerCase();
        bVal = (b.sourceLevel ?? '').toLowerCase();
      } else {
        aVal = (a.status ?? '').toLowerCase();
        bVal = (b.status ?? '').toLowerCase();
      }
      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredRows, sortKey, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / rowsPerPage));
  const pagedRows = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [sortedRows, page, rowsPerPage]);

  const handleSort = useCallback((key: SortKeyType) => {
    if (sortKey === key) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
    setPage(1);
  }, [sortKey]);

  const resetAllFilters = useCallback(() => {
    setFilterStatus([]);
    setFilterLevel([]);
    setFilterComponent([]);
    setFilterConfigType([]);
    setQuery('');
    setPage(1);
  }, []);

  const isAllOnPageSelected = pagedRows.length > 0 && pagedRows.every((r) => selectedIds.has(r.selectionId));
  const isSomeOnPageSelected = pagedRows.some((r) => selectedIds.has(r.selectionId));

  const toggleAllOnPage = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        pagedRows.forEach((r) => next.add(r.selectionId));
      } else {
        pagedRows.forEach((r) => next.delete(r.selectionId));
      }
      return next;
    });
  };

  const toggleOne = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const selectedCount = selectedIds.size;

  const selectedCountByCategory = useMemo(() => {
    const counts: Record<string, number> = {};
    selectedIds.forEach((id) => {
      const category = id.includes('::') ? id.split('::')[0] : 'other';
      counts[category] = (counts[category] || 0) + 1;
    });
    return counts;
  }, [selectedIds]);

  const selectedBreakdown = useMemo(() => {
    const configCount = selectedCountByCategory['configs'] ?? 0;
    const stringResourceCount =
      (selectedCountByCategory['stringResources'] ?? 0) +
      Object.entries(selectedCountByCategory).reduce(
        (sum, [k, v]) => (k !== 'stringResources' && k !== 'configs' && k.toLowerCase().includes('string') ? sum + v : sum),
        0
      );
    const other = Object.entries(selectedCountByCategory).filter(
      ([k]) => k !== 'configs' && k !== 'stringResources' && !k.toLowerCase().includes('string')
    );
    return { configCount, stringResourceCount, other };
  }, [selectedCountByCategory]);

  const selectedPromotionItems = useMemo(() => {
    const items: any[] = [];
    const isEnvSection = (cat: string) => cat === 'environmentConfigs' || cat === 'environmentStrings';
    const pushTargetEnvId = targetEnv?.id != null ? Number(targetEnv.id) : null;
    const multiEnv = compareTargetEnvironments.length >= 2;
    // Group by (configType, configKey, levelDisplay) to ensure uniqueness - same configKey at different levels stay separate
    const configKey = (row: DiffRow) => {
      const fromMeta = (row.metadata?.configKey ?? '').replace(/__\d+$/, '').trim();
      if (fromMeta) return fromMeta;
      const raw = (row.key ?? '').replace(/__\d+$/, '').trim();
      if (raw.includes('::')) {
        const parts = raw.split('::');
        return parts.slice(1).join('::');
      }
      return raw || (row.key ?? '');
    };
    const envGroupKey = (row: DiffRow) => {
      const ct = row.metadata?.configType ?? row.category ?? '';
      const ck = configKey(row);
      const ld = formatLevelDisplay(row.metadata ?? {}, '—');
      return `${ct}::${ck}::${ld}`;
    };
    const envByKeyAndEnv = new Map<string, Map<number, { targetValue: any; status: string; row: DiffRow; fromEdit: boolean; sectionCategory: string }>>();
    for (const [category, rows] of Object.entries(sections)) {
      for (const row of rows) {
        if (!selectedIds.has(row.selectionId)) continue;
        const isEnv = isEnvSection(category);
        if (isEnv && multiEnv) {
          const groupKey = envGroupKey(row);
          for (const te of compareTargetEnvironments) {
            const envId = Number(te.id);
            const editedVal = nonprodNewValues[row.selectionId];
            const newVal = editedVal?.[envId] ?? editedVal?.[te.id as number];
            const target = row.targets?.find((t: TargetEntry) => t.envId === envId || t.envId === te.id);
            const dbValue = (target as any)?.dbValue != null ? String((target as any).dbValue) : '';
            const targetVal =
              newVal !== undefined && newVal !== ''
                ? newVal
                : (dbValue !== '' ? dbValue : (target?.targetValue ?? row.targetValueRaw));
            const fromEdit = newVal !== undefined && newVal !== '';
            const statusForEnv = target?.status ?? row.status;
            if (!envByKeyAndEnv.has(groupKey)) envByKeyAndEnv.set(groupKey, new Map());
            const envMap = envByKeyAndEnv.get(groupKey)!;
            const existing = envMap.get(envId);
            // Prefer edited value: set if no existing, or if this is edited and existing was not
            if (!existing || (fromEdit && !existing.fromEdit)) {
              envMap.set(envId, { targetValue: targetVal, status: statusForEnv, row, fromEdit, sectionCategory: category });
            }
          }
        } else {
          // environmentConfigs (single target) stores edits in nonprodNewValues; environmentStrings uses envVarEdits
          const envIdForEdit = pushTargetEnvId ?? (compareTargetEnvironments[0] ? Number(compareTargetEnvironments[0].id) : null);
          const editedTarget = isEnv
            ? (category === 'environmentConfigs' && envIdForEdit != null && nonprodNewValues[row.selectionId]?.[envIdForEdit] !== undefined
                ? nonprodNewValues[row.selectionId][envIdForEdit]
                : envVarEdits[row.selectionId])
            : undefined;
          const targetForEnv = pushTargetEnvId != null && row.targets?.length
            ? row.targets.find((t: TargetEntry) => t.envId === pushTargetEnvId)
            : null;
          const dbValue = (targetForEnv as any)?.dbValue != null ? String((targetForEnv as any).dbValue) : '';
          const targetValue =
            editedTarget !== undefined
              ? editedTarget
              : (dbValue !== '' ? dbValue : (targetForEnv?.targetValue ?? row.targetValueRaw));
          const statusForEnv =
            pushTargetEnvId != null && row.targets?.length
              ? row.targets.find((t: TargetEntry) => t.envId === pushTargetEnvId)?.status ?? row.status
              : row.status;
          const envIdForItem = pushTargetEnvId ?? (compareTargetEnvironments[0] ? Number(compareTargetEnvironments[0].id) : null);
          const targetsWithOldValue =
            multiEnv && row.targets?.length
              ? row.targets.map((t: TargetEntry) => ({
                  ...t,
                  targetValue: targetValue,
                  oldValue: t.targetValue,
                }))
              : row.targets;
          // Preserve full metadata (tags, productType, fuelType, clusterReference, etc.) for push/shell-bundle payload
          const itemMetadata = isEnv
            ? { ...row.metadata, configKey: row.metadata?.configKey ?? row.key, configType: row.metadata?.configType ?? row.category, levelDisplay: formatLevelDisplay(row.metadata ?? {}, '—'), isEnvVar: true }
            : row.metadata;
          items.push({
            category: isEnv ? category : category,
            key: row.key,
            status: statusForEnv,
            sourceValue: row.sourceValueRaw,
            targetValue,
            metadata: itemMetadata,
            conflictReason: row.conflictReason,
            // Include envId for single-target so Review page groups correctly (same as multi-target)
            ...(isEnv && !multiEnv && envIdForItem != null ? { envId: envIdForItem } : {}),
            // Include oldValue for single-target env items (createPR needs it per target)
            ...(isEnv && !multiEnv && envIdForItem != null && row.targets?.length
              ? { oldValue: row.targets.find((t: TargetEntry) => t.envId === envIdForItem || t.envId === pushTargetEnvId)?.targetValue }
              : {}),
            // Include targets so Review page can show all env columns (including prod). Add oldValue per target for createPR.
            ...(multiEnv && row.targets?.length ? { targets: targetsWithOldValue } : {}),
          });
        }
      }
    }
    // Emit one item per (groupKey, envId) for env sections so Review never gets duplicate envIds that overwrite user edits
    envByKeyAndEnv.forEach((envMap, groupKey) => {
      const firstEntry = Array.from(envMap.values())[0];
      const row = firstEntry?.row;
      const sectionCategory = firstEntry?.sectionCategory ?? 'environmentConfigs';
      if (!row) return;
      const ck = configKey(row);
      envMap.forEach(({ targetValue, status: statusForEnv }, envId) => {
        const oldValue = row.targets?.find((t: TargetEntry) => t.envId === envId || t.envId === Number(envId))?.targetValue;
        // Preserve full metadata (tags, productType, fuelType, clusterReference) for push/shell-bundle
        const envItemMetadata = { ...row.metadata, configKey: ck, configType: row.metadata?.configType ?? row.category, levelDisplay: formatLevelDisplay(row.metadata ?? {}, '—'), isEnvVar: true };
        items.push({
          category: sectionCategory,
          key: row.key,
          status: statusForEnv,
          sourceValue: row.sourceValueRaw,
          targetValue,
          metadata: envItemMetadata,
          conflictReason: row.conflictReason,
          envId,
          ...(oldValue !== undefined && oldValue !== null ? { oldValue } : {}),
        });
      });
    });
    return items;
  }, [sections, selectedIds, envVarEdits, nonprodNewValues, compareTargetEnvironments, targetEnv?.id]);

  const effectivePolicy = useMemo(() => computeEffectivePolicy(matchedMatrixRule), [matchedMatrixRule]);

  const deploymentAction: DeploymentAction = effectivePolicy.action;


  const handleDeploy = useCallback(async () => {
    if (selectedCount === 0) {
      toast.error('Select at least one item to proceed.');
      return;
    }
    if (!effectivePolicy.allowed) {
      toast.error(effectivePolicy.reason || 'Sync not permitted for this environment pair.');
      return;
    }
    if (!sourceEnv || !targetEnv) return;
    const projectId = selectedPilot?.projectId;
    if (!projectId) {
      toast.error('Project ID missing. Select a project first.');
      return;
    }
    if (selectedPromotionItems.length === 0) {
      toast.error('No items found for selection. Try selecting again.');
      return;
    }

    rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.CONFIG_PROMOTION_INITIATED, {
      deployment_action: deploymentAction,
      item_count: selectedPromotionItems.length,
      source_env: sourceEnv.type ?? '',
      target_env: targetEnv.type ?? '',
    });
    setIsDeploying(true);
    setShellBundle(null);
    setSyncId(null);
    setSyncStatus(null);
    try {
      const pushTargetId = Number(targetEnv.id);
      const itemsForPush =
        deploymentAction === 'PUSH'
          ? selectedPromotionItems.filter((item: any) => item.envId == null || item.envId === pushTargetId)
          : selectedPromotionItems;
      // itemsForPush must carry full metadata (configType, configKey, productType, fuelType, clusterReference, tags) for backend alignment with compare API
      const payload = {
        projectId,
        items: itemsForPush,
        sourceEnvId: Number(sourceEnv.id),
        targetEnvId: pushTargetId,
      };

      if (deploymentAction === 'PUSH') {
        const resp = await PilotApis.pushConfigToolsSync(rcRef.current, payload);
        if (resp.error) {
          const msg =
            typeof resp.error === 'string'
              ? resp.error
              : (resp.error.message || (resp.error.code ? `${resp.error.code}` : '') || 'Push failed');
          const suffix = typeof resp.error === 'object' && resp.error?.code ? ` (${resp.error.code})` : '';
          rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.CONFIG_PROMOTION_FAILED, {
            deployment_action: deploymentAction,
            error_type: 'api_error',
            severity: 'critical',
          });
          toast.error(`${msg}${suffix}`.trim());
          return;
        }
        const newSyncId = resp.data?.syncId ? String(resp.data.syncId) : null;
        // no auto-polling: user can manually refresh status
        setSyncId(newSyncId);
        setSyncStatus(resp.data || null);
        rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.CONFIG_PROMOTION_EXECUTED, {
          deployment_action: deploymentAction,
          item_count: itemsForPush.length,
        });
        toast.success(newSyncId ? `Push started (syncId: ${newSyncId}).` : 'Push started.');
      } else {
        // Backend always returns ZIP file (binary data)
        setIsCreatingZipBundle(true);
        try {
          const targetEnvs =
            compareTargetEnvironments.length > 0
              ? compareTargetEnvironments
              : [
                  {
                    id: Number(targetEnv.id),
                    environmentName: (targetEnv as any).environmentName ?? `Environment ${targetEnv.id}`,
                    type: (targetEnv as any).type ?? 'prod',
                  },
                ];

          const shellBundlePayload = buildShellBundlePayload(
            selectedPromotionItems,
            targetEnvs,
            Number(sourceEnv.id),
            String(projectId)
          );

          const resp = await PilotApis.createConfigToolsShellBundle(rcRef.current, shellBundlePayload);

          if (resp.error) {
            const msg =
              typeof resp.error === 'string'
                ? resp.error
                : (resp.error.message || (resp.error.code ? `${resp.error.code}` : '') || 'Create PR (shell bundle) failed');
            const suffix = typeof resp.error === 'object' && resp.error?.code ? ` (${resp.error.code})` : '';
            setIsCreatingZipBundle(false);
            rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.CONFIG_PROMOTION_FAILED, {
              deployment_action: deploymentAction,
              error_type: 'api_error',
              severity: 'critical',
            });
            toast.error(`${msg}${suffix}`.trim());
            return;
          }

          // Backend returned ZIP file as binary blob
          if (!resp.data) {
            setIsCreatingZipBundle(false);
            toast.error('No data received from server.');
            return;
          }

          // Ensure we have a Blob
          let zipBlob: Blob | null = null;
          if (resp.data instanceof Blob) {
            zipBlob = resp.data;
          } else if (resp.data instanceof ArrayBuffer) {
            zipBlob = new Blob([resp.data], { type: 'application/zip' });
          } else if (typeof resp.data === 'string') {
            try {
              const parsed = JSON.parse(resp.data);
              if (parsed.error || parsed.message) {
                throw new Error(parsed.error || parsed.message || 'Server returned error');
              }
            } catch (e) {
              if (e instanceof Error && e.message.includes('Server returned error')) {
                throw e;
              }
              zipBlob = new Blob([resp.data], { type: 'application/zip' });
            }
          } else {
            zipBlob = new Blob([resp.data], { type: 'application/zip' });
          }

          if (!zipBlob) {
            setIsCreatingZipBundle(false);
            toast.error('Failed to process zip file from server.');
            return;
          }

          if (zipBlob.size === 0) {
            setIsCreatingZipBundle(false);
            toast.error('Received empty zip file from server.');
            return;
          }

          let filename = `shell-bundle-${new Date().getTime()}.zip`;
          if (resp.headers) {
            const contentDisposition =
              resp.headers['content-disposition'] ||
              resp.headers['Content-Disposition'] ||
              (typeof resp.headers.get === 'function' ? resp.headers.get('content-disposition') : null);

            if (contentDisposition && typeof contentDisposition === 'string') {
              const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
              if (match && match[1]) {
                filename = match[1].replace(/['"]/g, '').trim();
              }
            }
          }

          const link = document.createElement('a');
          link.href = URL.createObjectURL(zipBlob);
          link.download = filename;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(link.href);

          setIsCreatingZipBundle(false);
          rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.CONFIG_PROMOTION_EXECUTED, {
            deployment_action: deploymentAction,
            item_count: selectedPromotionItems.length,
          });
          toast.success(`Shell bundle zip downloaded: ${filename}`);
        } catch (error) {
          console.error('Error creating/downloading shell bundle zip:', error);
          setIsCreatingZipBundle(false);
          rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.CONFIG_PROMOTION_FAILED, {
            deployment_action: deploymentAction,
            error_type: 'api_error',
            severity: 'critical',
          });
          toast.error('Failed to create or download shell bundle. Try again.');
        }
      }
    } finally {
      setIsDeploying(false);
    }
  }, [sourceEnv, targetEnv, selectedPilot, selectedPromotionItems, deploymentAction, compareTargetEnvironments, effectivePolicy, toast]);

  const refreshSyncStatus = useCallback(async () => {
    if (!syncId) return;
    setIsLoadingSyncStatus(true);
    try {
      const resp = await PilotApis.getConfigToolsSyncStatus(rcRef.current, syncId);
      if (resp.error) {
        toast.error(resp.error.message || 'Failed to fetch push status');
        return;
      }
      setSyncStatus(resp.data || null);
    } finally {
      setIsLoadingSyncStatus(false);
    }
  }, [syncId, toast]);

  /** Returns true if value is considered empty for environment config (null, undefined, or blank string). */
  const isEnvConfigValueEmpty = useCallback((v: any): boolean => {
    if (v === null || v === undefined) return true;
    if (typeof v === 'string') return v.trim() === '';
    return false;
  }, []);

  const handleNavigateToReview = useCallback(() => {
    if (selectedCount === 0) {
      toast.error('Select at least one item to proceed.');
      return;
    }
    if (!effectivePolicy.allowed) {
      toast.error(effectivePolicy.reason || 'Sync not permitted for this environment pair.');
      return;
    }
    if (!sourceEnv || !targetEnv) return;
    const projectId = selectedPilot?.projectId;
    if (!projectId) {
      toast.error('Project ID missing. Select a project first.');
      return;
    }
    if (selectedPromotionItems.length === 0) {
      toast.error('No items found for selection. Try selecting again.');
      return;
    }
    // FE validation: environmentConfigs – every selected row must have a non-empty new value
    const envConfigRows = sections['environmentConfigs'] ?? [];
    const pushTargetEnvId = targetEnv?.id != null ? Number(targetEnv.id) : null;
    const multiEnv = compareTargetEnvironments.length >= 2;
    for (const row of envConfigRows) {
      if (!selectedIds.has(row.selectionId)) continue;
      if (multiEnv) {
        for (const te of compareTargetEnvironments) {
          const envId = Number(te.id);
          const editedVal = nonprodNewValues[row.selectionId];
          const newVal = editedVal?.[envId] ?? editedVal?.[te.id as number];
          const target = row.targets?.find((t: TargetEntry) => t.envId === envId || t.envId === te.id);
          const dbValue = (target as any)?.dbValue != null ? String((target as any).dbValue) : '';
          const targetVal =
            newVal !== undefined && newVal !== ''
              ? newVal
              : (dbValue !== '' ? dbValue : (target?.targetValue ?? row.targetValueRaw));
          if (isEnvConfigValueEmpty(targetVal)) {
            toast.error('Please enter a new value for all selected Environment Configs before proceeding.');
            return;
          }
        }
      } else {
        const envIdForEdit = pushTargetEnvId ?? (compareTargetEnvironments[0] ? Number(compareTargetEnvironments[0].id) : null);
        const editedTarget =
          envIdForEdit != null && nonprodNewValues[row.selectionId]?.[envIdForEdit] !== undefined
            ? nonprodNewValues[row.selectionId][envIdForEdit]
            : undefined;
        const targetForEnv =
          pushTargetEnvId != null && row.targets?.length
            ? row.targets.find((t: TargetEntry) => t.envId === pushTargetEnvId)
            : null;
        const dbValue = (targetForEnv as any)?.dbValue != null ? String((targetForEnv as any).dbValue) : '';
        const targetValue =
          editedTarget !== undefined
            ? editedTarget
            : (dbValue !== '' ? dbValue : (targetForEnv?.targetValue ?? row.targetValueRaw));
        if (isEnvConfigValueEmpty(targetValue)) {
          toast.error('Please enter a new value for all selected Environment Configs before proceeding.');
          return;
        }
      }
    }
    rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.CONFIG_PROMOTION_REVIEW_OPENED, {
      deployment_action: deploymentAction,
      item_count: selectedPromotionItems.length,
    });
    setIsNavigatingToReview(true);
    requestAnimationFrame(async () => {
      try {
        const payload = {
          projectId: String(projectId),
          items: selectedPromotionItems,
          sourceEnvId: Number(sourceEnv.id),
          targetEnvId: Number(targetEnv.id),
          deploymentAction,
          sourceEnvLabel: getEnvLabel(sourceEnv),
          targetEnvLabel: getEnvLabel(targetEnv),
          projectName: (selectedPilot as any)?.name ?? (selectedPilot as any)?.projectName ?? '',
          targetEnvironments: compareTargetEnvironments,
        };
        sessionStorage.setItem(PENDING_PROMOTION_SESSION_KEY, JSON.stringify(payload));
      } catch (e) {
        setIsNavigatingToReview(false);
        toast.error('Failed to open review. Try again.');
        return;
      }
      try {
        const snapshot = {
          projectId: String(projectId),
          sourceEnvId: String(sourceEnv.id),
          targetEnvId: String(targetEnv.id),
          targetEnvironments: compareTargetEnvironments,
          sections,
          sectionSummaries,
          sectionKey,
          selectedIds: Array.from(selectedIds),
          page,
          rowsPerPage,
          query,
          onlyDifferences,
          sortKey,
          sortOrder,
          filterStatus,
          filterLevel,
          filterComponent,
          filterConfigType,
          envVarEdits,
          nonprodNewValues,
        };
        await setConfigCompareSnapshot(snapshot);
      } catch {
        await removeConfigCompareSnapshot();
        setIsNavigatingToReview(false);
        toast.error('Failed to save compare state. Try again.');
        return;
      }
      rc.uiRouter.navigate(ROUTE_TREE.CONFIG_TOOLS.CONFIG_COMPARE_REVIEW);
    });
  }, [
    selectedCount,
    effectivePolicy,
    sourceEnv,
    targetEnv,
    selectedPilot,
    selectedPromotionItems,
    deploymentAction,
    compareTargetEnvironments,
    sections,
    sectionSummaries,
    sectionKey,
    selectedIds,
    page,
    rowsPerPage,
    query,
    onlyDifferences,
    sortKey,
    sortOrder,
    filterStatus,
    filterLevel,
    filterComponent,
    filterConfigType,
    envVarEdits,
    nonprodNewValues,
    isEnvConfigValueEmpty,
    toast,
    rc.uiRouter,
  ]);

  /** First click: switch to Environment Configs tab (if not already). Second click (when on that tab): go to Review. If no env tab exists, go to Review in one click. */
  const handleConfirmOrDeploy = useCallback(() => {
    if (sectionKey === 'environmentConfigs') {
      handleNavigateToReview();
      return;
    }
    if (sections['environmentConfigs'] && sections['environmentConfigs'].length > 0) {
      setSectionKey('environmentConfigs');
      setOpenEnvConfigsAttentionModal(true);
    } else {
      handleNavigateToReview();
    }
  }, [sectionKey, sections, handleNavigateToReview]);

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, position: 'relative' }}>
      {isNavigatingToReview && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.85)',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={48} />
            <Typography variant="body1" fontWeight={600} color="text.secondary">
              Opening Review…
            </Typography>
          </Box>
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box sx={{ textAlign: 'left' }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Compare &amp; Sync Environments
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Compare and promote configuration changes between environments.
          </Typography>
        </Box>
        <StudioButton variant="outline" onClick={() => rc.uiRouter.navigate(ROUTE_TREE.PROJECT.DETAILS)}>
          Back to Project Details
        </StudioButton>
      </Box>

      {!selectedPilot?.projectId && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Select a project first (Project Details). Config Compare uses the environments associated with the selected project.
        </Alert>
      )}

      <Card sx={{ borderRadius: 2, boxShadow: 0, border: '1px solid', borderColor: 'divider', mb: 2 }}>
        <CardHeader
          sx={{ textAlign: 'left' }}
          title={<Typography sx={{ fontWeight: 700 }}>Environment Selection</Typography>}
          subheader={
            <Typography variant="body2" color="text.secondary">
              Compare and promote configuration changes between environments.
            </Typography>
          }
        />
        <CardContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, alignItems: 'end' }}>
            <StudioField label="Source Environment (Push From)">
              <BglSelect
                value={sourceEnvId || ''}
                onChange={(v) => {
                  const next = v === '' ? '' : String(v);
                  setSourceEnvId(next);
                  setTargetEnvId('');
                  setViewMode('selection');
                  setSelectedIds(new Set());
                  setPage(1);
                }}
                options={sourceEnvOptions}
                placeholder="Source"
                minWidth={220}
                disabled
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'action.disabledBackground',
                    color: 'text.secondary',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                  },
                  '& .MuiSelect-icon': { color: 'text.disabled' },
                }}
              />
            </StudioField>

            <StudioField label="Target Environment (Sync To)">
              <BglSelect
                value={targetEnvId || ''}
                onChange={(v) => {
                  const next = v === '' ? '' : String(v);
                  setTargetEnvId(next);
                  if (next) {
                    rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.CONFIG_COMPARE_VERSION_SELECTED, {
                      env_role: 'target',
                    });
                  }
                  setViewMode('selection');
                  setSelectedIds(new Set());
                  setPage(1);
                }}
                options={targetEnvOptions}
                placeholder="Select target…"
                minWidth={220}
                disabled={isLoadingEnvs || !sourceEnvId || availableTargetEnvs.length === 0 || isComparing}
                loading={isLoadingEnvs}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderWidth: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderWidth: 2,
                      borderColor: 'primary.main',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.dark',
                      borderWidth: 2,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderWidth: 2,
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
            </StudioField>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2, gap: 2 }}>
            {isLoadingEnvs ? (
              <Typography variant="body2" color="text.secondary">
                <CircularProgress size={14} /> Loading environments…
              </Typography>
            ) : null}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', justifyContent: 'center' }}>
              <PermissionGate require={UI_PAGES.CONFIG_COMPARE.ctas.compare.id} mode="disable">
                <StudioButton
                  variant="primary"
                  onClick={handleCompare}
                  disabled={!sourceEnv || !targetEnv || isComparing || isLoadingEnvs || (matchedMatrixRule ? !getRuleProp(matchedMatrixRule, 'viewAccess', 'view_access', false) : false)}
                  startIcon={isComparing ? <CircularProgress size={16} /> : undefined}
                >
                  {isComparing ? 'Comparing…' : 'Compare Configurations'}
                </StudioButton>
              </PermissionGate>
              <StudioButton
                variant="outline"
                onClick={() => setViewMode('history')}
                disabled={!sourceEnv || !targetEnv || isLoadingEnvs}
              >
                View History
              </StudioButton>
            </Box>
            {sourceEnv && targetEnv && !effectivePolicy.allowed && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {effectivePolicy.reason}
              </Alert>
            )}
          </Box>
        </CardContent>
      </Card>

      {isComparing && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 6,
            gap: 2,
          }}
        >
          <CircularProgress size={48} />
          <Typography variant="body2" color="text.secondary">
            Loading comparison data…
          </Typography>
        </Box>
      )}

      {viewMode === 'compare' && !isComparing && (
        <>
        <Card sx={{ borderRadius: 2, boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
          <CardHeader
            sx={{ textAlign: 'left' }}
            title={<Typography sx={{ fontWeight: 700 }}>Difference Catalogue</Typography>}
            subheader={
              <Box sx={{ textAlign: 'left', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {sourceEnv && targetEnv ? (
                  <>
                    <Box component="span" sx={{ px: 1, py: 0.25, borderRadius: 1, border: '1px solid', borderColor: 'divider', fontSize: 12, fontWeight: 600 }}>
                      {getEnvLabel(sourceEnv)}
                    </Box>
                    <Typography component="span" sx={{ fontSize: 14, color: 'text.secondary' }}>→</Typography>
                    <Box component="span" sx={{ px: 1, py: 0.25, borderRadius: 1, border: '1px solid', borderColor: 'divider', fontSize: 12, fontWeight: 600 }}>
                      {getEnvLabel(targetEnv)}
                    </Box>
                  </>
                ) : null}
              </Box>
            }
            action={
              <StudioButton variant="outline" onClick={() => setViewMode('selection')} startIcon={<ChevronLeftIcon sx={{ fontSize: 18 }} />}>
                Environment Selection
              </StudioButton>
            }
          />
          <CardContent>
            <Tabs
              value={sectionKey}
              onChange={(_, v) => setSectionKey(String(v))}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 1, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 }, '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' } }}
            >
              {Object.entries(sections).map(([k, rows]) => {
                const fc = filteredSectionCounts[k];
                const total = fc?.total ?? 0;
                const conflicts = fc?.conflicts ?? 0;
                const tabLabel =
                  k === 'environmentConfigs'
                    ? 'Environment Configs'
                    : k === 'environmentStrings'
                      ? 'Environment Strings'
                      : k;
                const labelText = conflicts > 0 ? `${tabLabel} (${total}, ${conflicts} conflict)` : `${tabLabel} (${total})`;
                return (
                  <Tab
                    key={k}
                    value={k}
                    label={labelText}
                  />
                );
              })}
            </Tabs>

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
              <Box sx={{ minWidth: 280, flex: '1 1 280px' }}>
                <input
                  className={studioStyles.input}
                  placeholder="Search items…"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setPage(1);
                  }}
                />
              </Box>

              <Tooltip title="Keeps future parity when we add equal rows">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Checkbox checked={onlyDifferences} onChange={(e) => {
                    setOnlyDifferences(e.target.checked);
                    if (e.target.checked) {
                      rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.CONFIG_COMPARE_DIFFERENCE_HIGHLIGHTED);
                    }
                  }} />
                  <Typography variant="body2">Show only differences</Typography>
                </Box>
              </Tooltip>

              <Tooltip title="Filters">
                <PromotionFilter
                  filterOptions={{
                    statuses: filterOptions.statuses,
                    levels: filterOptions.levels,
                    configTypes: filterOptions.configTypes,
                    components: filterOptions.components,
                  }}
                  filterStatus={filterStatus}
                  filterLevel={filterLevel}
                  filterConfigType={filterConfigType}
                  filterComponent={filterComponent}
                  onFilterStatusChange={(v) => setFilterStatus(v)}
                  onFilterLevelChange={(v) => setFilterLevel(v)}
                  onFilterConfigTypeChange={(v) => setFilterConfigType(v)}
                  onFilterComponentChange={(v) => setFilterComponent(v)}
                  onResetAll={resetAllFilters}
                  onPageReset={() => setPage(1)}
                  includeComponent
                  buttonSize="sm"
                  topChips={
                    query ? (
                      <Chip
                        size="small"
                        label={`Search: "${query}"`}
                        onDelete={() => {
                          setQuery('');
                          setPage(1);
                        }}
                        sx={{ height: 28 }}
                      />
                    ) : undefined
                  }
                  panelContentBefore={
                    <>
                      <label
                        style={{
                          display: 'block',
                          fontSize: 11,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          color: 'inherit',
                          marginBottom: 12,
                          textAlign: 'left',
                        }}
                      >
                        Default Sorting
                      </label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
                        <div style={{ textAlign: 'left' }}>
                          <label
                            style={{
                              display: 'block',
                              fontSize: 11,
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              color: 'inherit',
                              marginBottom: 6,
                              textAlign: 'left',
                            }}
                          >
                            Sort Column
                          </label>
                          <BglSelect<SortKeyType>
                            value={sortKey}
                            onChange={(v) => v !== '' && setSortKey(v as SortKeyType)}
                            options={[
                              { value: 'configKey', label: 'Config Key' },
                              { value: 'configType', label: 'Config Type' },
                              { value: 'level', label: 'Level' },
                              { value: 'status', label: 'Status' },
                            ]}
                            placeholder="Sort Column"
                            sx={{ width: '100%' }}
                            menuZIndex={1400}
                          />
                        </div>
                        <div style={{ textAlign: 'left' }}>
                          <label
                            style={{
                              display: 'block',
                              fontSize: 11,
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              color: 'inherit',
                              marginBottom: 6,
                              textAlign: 'left',
                            }}
                          >
                            Sort Order
                          </label>
                          <div
                            role="radiogroup"
                            aria-label="Sort Order"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 16,
                              flexWrap: 'wrap',
                            }}
                          >
                            <label
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                cursor: 'pointer',
                                fontSize: 14,
                                color: 'inherit',
                              }}
                            >
                              <input
                                type="radio"
                                name="sortOrder"
                                value="asc"
                                checked={sortOrder === 'asc'}
                                onChange={() => setSortOrder('asc')}
                                style={{ accentColor: palette.primary.main }}
                              />
                              Ascending
                            </label>
                            <label
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                cursor: 'pointer',
                                fontSize: 14,
                                color: 'inherit',
                              }}
                            >
                              <input
                                type="radio"
                                name="sortOrder"
                                value="desc"
                                checked={sortOrder === 'desc'}
                                onChange={() => setSortOrder('desc')}
                                style={{ accentColor: palette.primary.main }}
                              />
                              Descending
                            </label>
                          </div>
                        </div>
                      </div>
                    </>
                  }
                />
              </Tooltip>
            </Box>

            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
              {/* Use a real table (like Studio) so columns align and don't "scatter" on wrap */}
              <Box
                component="table"
                sx={{
                  width: '100%',
                  tableLayout: 'fixed',
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                  '& th, & td': { boxSizing: 'border-box' },
                }}
              >
                <colgroup>
                  <col style={{ width: 44 }} />
                  {!(sectionKey === 'stringResources' || sectionKey.toLowerCase().includes('string')) && (
                    <col style={{ width: '12%' }} />
                  )}
                  <col style={{ width: sectionKey === 'stringResources' || sectionKey.toLowerCase().includes('string') ? '26%' : '16%' }} />
                  <col style={{ width: sectionKey === 'stringResources' || sectionKey.toLowerCase().includes('string') ? '22%' : '18%' }} />
                  <col style={{ width: '18%' }} />
                  {sectionKey === 'environmentConfigs' ? (
                    compareTargetEnvironments.length >= 2 ? (
                      <>
                        {compareTargetEnvironments.map((te) => (
                          <React.Fragment key={te.id}>
                            <col style={{ width: '12%' }} />
                            <col style={{ width: '12%' }} />
                          </React.Fragment>
                        ))}
                      </>
                    ) : (
                      <>
                        <col style={{ width: '12%' }} />
                        <col style={{ width: '12%' }} />
                      </>
                    )
                  ) : (
                    compareTargetEnvironments.length >= 2 ? (
                      <>
                        {compareTargetEnvironments.map((te) => (
                          <col key={te.id} style={{ width: '14%' }} />
                        ))}
                      </>
                    ) : (
                      <col style={{ width: '18%' }} />
                    )
                  )}
                  <col style={{ width: 130 }} />
                </colgroup>

                <Box component="thead" sx={{ bgcolor: 'action.hover', '& tr th': { verticalAlign: 'middle', textAlign: 'left', padding: '12px 8px' } }}>
                  <Box component="tr">
                    <Box component="th" sx={{ width: 44, minWidth: 44, padding: '12px 8px' }}>
                      <Checkbox
                        checked={isAllOnPageSelected}
                        indeterminate={!isAllOnPageSelected && isSomeOnPageSelected}
                        onChange={(e) => toggleAllOnPage(e.target.checked)}
                        // sx={{ p: 0 }}
                      />
                    </Box>
                    {!(sectionKey === 'stringResources' || sectionKey.toLowerCase().includes('string')) && (
                      <Box component="th" sx={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('configType')}>
                        <Typography variant="caption" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5, textAlign: 'left', lineHeight: 1.4 }}>
                          Config Type
                          {sortKey === 'configType' ? (sortOrder === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 14 }} /> : <ArrowDownwardIcon sx={{ fontSize: 14 }} />) : <UnfoldMoreIcon sx={{ fontSize: 14, opacity: 0.5 }} />}
                        </Typography>
                      </Box>
                    )}
                    <Box component="th" sx={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('configKey')}>
                      <Typography variant="caption" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5, textAlign: 'left', lineHeight: 1.4 }}>
                        {sectionKey === 'stringResources' || sectionKey.toLowerCase().includes('string') ? 'String key' : 'Config Key'}
                        {sortKey === 'configKey' ? (sortOrder === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 14 }} /> : <ArrowDownwardIcon sx={{ fontSize: 14 }} />) : <UnfoldMoreIcon sx={{ fontSize: 14, opacity: 0.5 }} />}
                      </Typography>
                    </Box>
                    <Box component="th" sx={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('level')}>
                      <Typography variant="caption" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5, textAlign: 'left', lineHeight: 1.4 }}>
                        Level
                        {sortKey === 'level' ? (sortOrder === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 14 }} /> : <ArrowDownwardIcon sx={{ fontSize: 14 }} />) : <UnfoldMoreIcon sx={{ fontSize: 14, opacity: 0.5 }} />}
                      </Typography>
                    </Box>
                    <Box component="th" sx={{ overflow: 'hidden', minWidth: 0 }} title={sourceEnv ? getEnvLabel(sourceEnv) : 'Source'}>
                      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', textAlign: 'left', lineHeight: 1.4, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                        {sourceEnv ? getEnvLabel(sourceEnv) : 'Source'} Value
                      </Typography>
                    </Box>
                    {sectionKey === 'environmentConfigs' ? (
                      compareTargetEnvironments.length >= 2 ? (
                        compareTargetEnvironments.map((te) => (
                          <React.Fragment key={te.id}>
                            <Box component="th" sx={{ overflow: 'hidden', minWidth: 0 }} title={te.environmentName}>
                              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', textAlign: 'left', lineHeight: 1.4, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                {te.environmentName || `Env ${te.id}`}{' '}
                                <Box component="span" sx={{ fontSize: 10, fontWeight: 700 }}>(Old Value)</Box>
                              </Typography>
                            </Box>
                            <Box component="th" sx={{ overflow: 'hidden', minWidth: 0 }} title={`${te.environmentName || `Env ${te.id}`} New Value`}>
                              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', textAlign: 'left', lineHeight: 1.4, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                {te.environmentName || `Env ${te.id}`}{' '}
                                <Box component="span" sx={{ fontSize: 10, fontWeight: 700 }}>(New Value)</Box>
                              </Typography>
                            </Box>
                          </React.Fragment>
                        ))
                      ) : (
                        <>
                          <Box component="th" sx={{ overflow: 'hidden', minWidth: 0 }} title={targetEnv ? getEnvLabel(targetEnv) : (compareTargetEnvironments[0]?.environmentName ?? 'Target')}>
                            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', textAlign: 'left', lineHeight: 1.4, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                              {(compareTargetEnvironments[0] ? compareTargetEnvironments[0].environmentName : null) || (targetEnv ? getEnvLabel(targetEnv) : 'Target')}{' '}
                              <Box component="span" sx={{ fontSize: 10, fontWeight: 700 }}>(Old Value)</Box>
                            </Typography>
                          </Box>
                          <Box component="th" sx={{ overflow: 'hidden', minWidth: 0 }} title={`${targetEnv ? getEnvLabel(targetEnv) : (compareTargetEnvironments[0]?.environmentName ?? 'Target')} New Value`}>
                            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', textAlign: 'left', lineHeight: 1.4, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                              {(compareTargetEnvironments[0] ? compareTargetEnvironments[0].environmentName : null) || (targetEnv ? getEnvLabel(targetEnv) : 'Target')}{' '}
                              <Box component="span" sx={{ fontSize: 10, fontWeight: 700 }}>(New Value)</Box>
                            </Typography>
                          </Box>
                        </>
                      )
                    ) : (
                      compareTargetEnvironments.length >= 2 ? (
                        compareTargetEnvironments.map((te) => (
                          <Box key={te.id} component="th" sx={{ overflow: 'hidden', minWidth: 0 }} title={te.environmentName}>
                            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', textAlign: 'left', lineHeight: 1.4, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                              {te.environmentName || `Env ${te.id}`} Value
                            </Typography>
                          </Box>
                        ))
                      ) : (
                        <Box component="th" sx={{ overflow: 'hidden', minWidth: 0 }} title={targetEnv ? getEnvLabel(targetEnv) : (compareTargetEnvironments[0]?.environmentName ?? 'Target')}>
                          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', textAlign: 'left', lineHeight: 1.4, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                            {(compareTargetEnvironments[0] ? compareTargetEnvironments[0].environmentName : null) || (targetEnv ? getEnvLabel(targetEnv) : 'Target')} Value
                          </Typography>
                        </Box>
                      )
                    )}
                    <Box component="th" sx={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('status')}>
                      <Typography variant="caption" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5, textAlign: 'left', lineHeight: 1.4 }}>
                        Status
                        {sortKey === 'status' ? (sortOrder === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 14 }} /> : <ArrowDownwardIcon sx={{ fontSize: 14 }} />) : <UnfoldMoreIcon sx={{ fontSize: 14, opacity: 0.5 }} />}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box component="tbody">
                  {pagedRows.length === 0 ? (
                    <Box component="tr">
                      <Box
                        component="td"
                        colSpan={
                          (sectionKey === 'stringResources' || sectionKey.toLowerCase().includes('string') ? 4 : 5) +
                          (sectionKey === 'environmentConfigs'
                            ? compareTargetEnvironments.length >= 2
                              ? 2 * compareTargetEnvironments.length
                              : 2
                            : compareTargetEnvironments.length >= 2
                              ? compareTargetEnvironments.length
                              : 1) +
                          1
                        }
                        sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider', textAlign: 'left' }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No differences match your filters.
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    pagedRows.map((row) => (
                      <Box
                        component="tr"
                        key={row.id}
                        sx={{
                          bgcolor: selectedIds.has(row.selectionId) ? 'rgba(0, 200, 180, 0.08)' : 'transparent',
                        }}
                      >
                        <Box component="td" sx={{ p: 1, verticalAlign: 'middle', borderTop: '1px solid', borderColor: 'divider', textAlign: 'left' }}>
                          <Checkbox
                            checked={selectedIds.has(row.selectionId)}
                            onChange={(e) => toggleOne(row.selectionId, e.target.checked)}
                          />
                        </Box>
                        {!(sectionKey === 'stringResources' || sectionKey.toLowerCase().includes('string')) && (
                          <Box component="td" sx={{ p: 1, verticalAlign: 'middle', borderTop: '1px solid', borderColor: 'divider', textAlign: 'left', maxWidth: 120 }}>
                            <Tooltip title={row.metadata?.configType ?? '—'} placement="top" enterDelay={300}>
                              <Typography
                                variant="body2"
                                component="span"
                                sx={{
                                  display: 'block',
                                  fontFamily: 'monospace',
                                  fontSize: theme.typography.body2.fontSize,
                                  color: 'text.secondary',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {row.metadata?.configType ?? '—'}
                              </Typography>
                            </Tooltip>
                          </Box>
                        )}
                        <Box component="td" sx={{ p: 1, verticalAlign: 'middle', borderTop: '1px solid', borderColor: 'divider', textAlign: 'left', overflow: 'hidden', minWidth: 0 }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0, maxWidth: '100%' }}>
                            <Tooltip title={row.metadata?.configKey ?? row.key} placement="top" enterDelay={300}>
                              <Typography
                                variant="body2"
                                component="span"
                                sx={{
                                  display: 'block',
                                  fontFamily: 'monospace',
                                  fontSize: theme.typography.body2.fontSize,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  minWidth: 0,
                                  maxWidth: '100%',
                                }}
                              >
                                {row.metadata?.configKey ?? row.key}
                              </Typography>
                            </Tooltip>
                            {(() => {
                              const configKeyForLookup = row.metadata?.configKey ?? row.key;
                              const entry = configKeyForLookup ? registryMap.get(String(configKeyForLookup)) : null;
                              if (!entry) return null;
                              const possibleStr = entry.possibleValues != null ? (typeof entry.possibleValues === 'string' ? entry.possibleValues : JSON.stringify(entry.possibleValues)) : '';
                              const hasAny = entry.configDocumentation || possibleStr || entry.component || entry.displayName;
                              return (
                                <Tooltip
                                  slotProps={{
                                    tooltip: {
                                      sx: {
                                        maxWidth: 360,
                                        borderRadius: 2.5,
                                        px: 2,
                                        py: 1.5,
                                        boxShadow: theme.shadows[8],
                                        bgcolor: 'background.paper',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                      },
                                    },
                                  }}
                                  title={
                                    <Box sx={{ display: 'block', maxWidth: 320 }}>
                                      {entry.configDocumentation ? (
                                        <Box sx={{ mb: 1.5 }}>
                                          <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.25 }}>
                                            Config Description
                                          </Typography>
                                          <Typography component="span" variant="body2" sx={{ color: 'text.primary' }}>
                                            {entry.configDocumentation}
                                          </Typography>
                                        </Box>
                                      ) : null}
                                      {possibleStr ? (
                                        <Box sx={{ mb: 1.5 }}>
                                          <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.25 }}>
                                            Accepted Values
                                          </Typography>
                                          <Typography component="span" variant="body2" sx={{ color: 'text.primary', fontFamily: 'monospace', fontStyle: 'italic' }}>
                                            {possibleStr}
                                          </Typography>
                                        </Box>
                                      ) : null}
                                      {(entry.component || entry.displayName) ? (
                                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                          {entry.component ? (
                                            <Box>
                                              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.25 }}>
                                                Component
                                              </Typography>
                                              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                                {entry.component}
                                              </Typography>
                                            </Box>
                                          ) : null}
                                          {entry.displayName ? (
                                            <Box>
                                              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.25 }}>
                                                Display Name
                                              </Typography>
                                              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                                {entry.displayName}
                                              </Typography>
                                            </Box>
                                          ) : null}
                                        </Box>
                                      ) : null}
                                      {!hasAny ? (
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                          No registry entry
                                        </Typography>
                                      ) : null}
                                    </Box>
                                  }
                                >
                                  <InfoOutlinedIcon sx={{ fontSize: 16, color: 'action.active', cursor: 'help' }} aria-label="Config key info" />
                                </Tooltip>
                              );
                            })()}
                          </Box>
                        </Box>
                        <Box component="td" sx={{ p: 1, verticalAlign: 'middle', borderTop: '1px solid', borderColor: 'divider', textAlign: 'left', overflow: 'hidden', minWidth: 0 }}>
                          <Tooltip title={row.sourceLevel} placement="top" enterDelay={300}>
                            <Box
                              component="span"
                              sx={{
                                display: 'block',
                                px: 1,
                                py: 0.25,
                                borderRadius: 9999,
                                border: '1px solid',
                                borderColor: 'divider',
                                fontSize: theme.typography.body2.fontSize,
                                fontFamily: 'monospace',
                                color: 'text.secondary',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '100%',
                              }}
                            >
                              {row.sourceLevel}
                            </Box>
                          </Tooltip>
                        </Box>
                        <Box component="td" sx={{ p: 1, verticalAlign: 'middle', borderTop: '1px solid', borderColor: 'divider', textAlign: 'left' }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: 12,
                              color: row.status === 'new' ? 'text.secondary' : 'success.main',
                              whiteSpace: 'pre-wrap',
                              overflowWrap: 'anywhere',
                              wordBreak: 'break-word',
                              lineHeight: 1.35,
                            }}
                          >
                            {row.sourceValueDisplay === 'Not Set' ? (
                              <Box component="span" sx={{ color: '#9CA3AF', fontStyle: 'italic' }}>Not Set</Box>
                            ) : (
                              row.sourceValueDisplay
                            )}
                          </Typography>
                        </Box>
                        {sectionKey === 'environmentConfigs' ? (
                          compareTargetEnvironments.length >= 2 ? (
                            compareTargetEnvironments.map((te, ti) => {
                              const t = row.targets && row.targets[ti] ? row.targets[ti] : null;
                              // Not Set only when key is not defined in that env (null/undefined); empty value = empty text.
                              const tDisplay = t == null || t.targetValue === null || typeof t.targetValue === 'undefined' ? 'Not Set' : safeStringify(t.targetValue);
                              const dbValue = (t as any)?.dbValue != null ? String((t as any).dbValue) : '';
                              const currentValue = tDisplay !== 'Not Set' ? tDisplay : '';
                              const newValue = nonprodNewValues[row.selectionId]?.[te.id] ?? dbValue ?? currentValue;
                              return (
                                <React.Fragment key={te.id}>
                                  <Box component="td" sx={{ p: 1, verticalAlign: 'middle', borderTop: '1px solid', borderColor: 'divider', textAlign: 'left' }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontFamily: 'monospace',
                                        fontSize: 12,
                                        whiteSpace: 'pre-wrap',
                                        overflowWrap: 'anywhere',
                                        wordBreak: 'break-word',
                                        lineHeight: 1.35,
                                        color: 'text.secondary',
                                      }}
                                    >
                                      {tDisplay === 'Not Set' ? (
                                        <Box component="span" sx={{ color: '#9CA3AF', fontStyle: 'italic' }}>Not Set</Box>
                                      ) : (
                                        tDisplay
                                      )}
                                    </Typography>
                                  </Box>
                                  <Box component="td" sx={{ p: 1, verticalAlign: 'middle', borderTop: '1px solid', borderColor: 'divider', textAlign: 'left' }}>
                                    <input
                                      className={studioStyles.input}
                                      value={newValue}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setNonprodNewValues((prev) => ({
                                          ...prev,
                                          [row.selectionId]: {
                                            ...(prev[row.selectionId] || {}),
                                            [te.id]: val,
                                          },
                                        }));
                                      }}
                                      style={{
                                        width: '100%',
                                        minHeight: 36,
                                        fontFamily: 'monospace',
                                        fontSize: 12,
                                        boxSizing: 'border-box',
                                      }}
                                      placeholder="New value"
                                      aria-label={`${te.environmentName || `Env ${te.id}`} New Value`}
                                    />
                                  </Box>
                                </React.Fragment>
                              );
                            })
                          ) : (
                            <>
                              <Box component="td" sx={{ p: 1, verticalAlign: 'middle', borderTop: '1px solid', borderColor: 'divider', textAlign: 'left' }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize: 12,
                                    whiteSpace: 'pre-wrap',
                                    overflowWrap: 'anywhere',
                                    wordBreak: 'break-word',
                                    lineHeight: 1.35,
                                    color: 'text.secondary',
                                  }}
                                >
                                  {row.targetValueDisplay === 'Not Set' ? (
                                    <Box component="span" sx={{ color: '#9CA3AF', fontStyle: 'italic' }}>Not Set</Box>
                                  ) : (
                                    row.targetValueDisplay
                                  )}
                                </Typography>
                              </Box>
                              <Box component="td" sx={{ p: 1, verticalAlign: 'middle', borderTop: '1px solid', borderColor: 'divider', textAlign: 'left' }}>
                                <input
                                  className={studioStyles.input}
                                  value={
                                    nonprodNewValues[row.selectionId]?.[targetEnv?.id ? Number(targetEnv.id) : (compareTargetEnvironments[0]?.id ?? 0)] ??
                                    (() => {
                                      const envId = targetEnv?.id ? Number(targetEnv.id) : (compareTargetEnvironments[0]?.id ?? 0);
                                      const target = row.targets?.find((t: TargetEntry) => t.envId === envId);
                                      const dbValue = (target as any)?.dbValue != null ? String((target as any).dbValue) : '';
                                      return dbValue || (row.targetValueDisplay !== 'Not Set' ? row.targetValueDisplay : '');
                                    })()
                                  }
                                  onChange={(e) => {
                                    const envId = targetEnv?.id ? Number(targetEnv.id) : (compareTargetEnvironments[0]?.id ?? 0);
                                    setNonprodNewValues((prev) => ({
                                      ...prev,
                                      [row.selectionId]: {
                                        ...(prev[row.selectionId] || {}),
                                        [envId]: e.target.value,
                                      },
                                    }));
                                  }}
                                  style={{
                                    width: '100%',
                                    minHeight: 36,
                                    fontFamily: 'monospace',
                                    fontSize: 12,
                                    boxSizing: 'border-box',
                                  }}
                                  placeholder="New value"
                                  aria-label="New Value"
                                />
                              </Box>
                            </>
                          )
                        ) : (
                          compareTargetEnvironments.length >= 2 ? (
                          compareTargetEnvironments.map((te, ti) => {
                            const t = row.targets && row.targets[ti] ? row.targets[ti] : null;
                            // Not Set only when key is not defined in that env (null/undefined); empty value = empty text.
                            const tDisplay = t == null || t.targetValue === null || typeof t.targetValue === 'undefined' ? 'Not Set' : safeStringify(t.targetValue);
                            const tStatus = (t?.status ?? 'unchanged') as DiffRow['status'];
                            return (
                              <Box key={te.id} component="td" sx={{ p: 1, verticalAlign: 'middle', borderTop: '1px solid', borderColor: 'divider', textAlign: 'left' }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word', lineHeight: 1.35, color: tStatus === 'modified' ? 'error.main' : 'text.secondary' }}>
                                  {tDisplay === 'Not Set' ? (
                                    <Box component="span" sx={{ color: '#9CA3AF', fontStyle: 'italic' }}>Not Set</Box>
                                  ) : (
                                    tDisplay
                                  )}
                                </Typography>
                              </Box>
                            );
                          })
                        ) : (
                          <Box component="td" sx={{ p: 1, verticalAlign: 'middle', borderTop: '1px solid', borderColor: 'divider', textAlign: 'left' }}>
                            {(sectionKey === 'environmentConfigs' || sectionKey === 'environmentStrings') ? (
                              <input
                                className={studioStyles.input}
                                value={envVarEdits[row.selectionId] ?? row.targetValueDisplay}
                                onChange={(e) =>
                                  setEnvVarEdits((prev) => ({ ...prev, [row.selectionId]: e.target.value }))
                                }
                                style={{
                                  width: '100%',
                                  minHeight: 36,
                                  fontFamily: 'monospace',
                                  fontSize: 12,
                                  boxSizing: 'border-box',
                                }}
                                placeholder="Target value"
                              />
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: 'monospace',
                                  fontSize: 12,
                                  color: row.status === 'modified' ? 'error.main' : 'text.secondary',
                                  whiteSpace: 'pre-wrap',
                                  overflowWrap: 'anywhere',
                                  wordBreak: 'break-word',
                                  lineHeight: 1.35,
                                }}
                              >
                                {row.targetValueDisplay === 'Not Set' ? (
                                  <Box component="span" sx={{ color: '#9CA3AF', fontStyle: 'italic' }}>Not Set</Box>
                                ) : (
                                  row.targetValueDisplay
                                )}
                              </Typography>
                            )}
                          </Box>
                        )
                        )}
                        <Box component="td" sx={{ p: 1, verticalAlign: 'middle', borderTop: '1px solid', borderColor: 'divider', textAlign: 'left' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                            <Box
                              component="span"
                              sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: 999,
                                fontSize: 12,
                                border: '1px solid',
                                borderColor:
                                  row.status === 'new'
                                    ? 'success.main'
                                    : row.status === 'conflict'
                                      ? 'error.main'
                                      : row.status === 'modified'
                                        ? 'warning.main'
                                        : row.status === 'target_only'
                                          ? 'info.main'
                                          : 'divider',
                                color:
                                  row.status === 'new'
                                    ? 'success.main'
                                    : row.status === 'conflict'
                                      ? 'error.main'
                                      : row.status === 'modified'
                                        ? 'warning.main'
                                        : row.status === 'target_only'
                                          ? 'info.main'
                                          : 'text.secondary',
                                fontWeight: 600
                              }}
                              title={row.status === 'conflict' && row.conflictReason ? row.conflictReason : undefined}
                            >
                              {row.status.toUpperCase()}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    ))
                  )}
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {sortedRows.length === 0 ? 0 : (page - 1) * rowsPerPage + 1} – {Math.min(page * rowsPerPage, sortedRows.length)} of {sortedRows.length} differences
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'uppercase' }}>Rows:</Typography>
                  <Select
                    size="small"
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setPage(1);
                    }}
                    sx={{ minWidth: 70, height: 32 }}
                  >
                    {[10, 20, 50, 100, 300, 500].map((n) => (
                      <MenuItem key={n} value={n}>{n}</MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <StudioButton
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </StudioButton>
                {(() => {
                  const start = totalPages <= 10 ? 1 : Math.max(1, Math.min(page - 5, totalPages - 9));
                  return Array.from({ length: Math.min(10, totalPages) }, (_, i) => {
                    const pageNum = start + i;
                    if (pageNum > totalPages) return null;
                    return (
                      <StudioButton
                        key={pageNum}
                        variant={page === pageNum ? 'primary' : 'outline'}
                        size="sm"
                        onClick={() => setPage(pageNum)}
                      >
                        {pageNum}
                      </StudioButton>
                    );
                  });
                })()}
                <StudioButton
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </StudioButton>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: '1 1 auto', minWidth: 0, textAlign: 'left' }}>
                {selectedCount === 0 ? (
                  <Alert severity="info" sx={{ py: 0.5, '& .MuiAlert-message': { py: 0.5 } }}>
                    Select items from the list above to begin synchronization.
                  </Alert>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Selected:{' '}
                    {[
                      selectedBreakdown.configCount > 0 && (
                        <span key="configs"><b>{selectedBreakdown.configCount}</b> configs</span>
                      ),
                      selectedBreakdown.stringResourceCount > 0 && (
                        <span key="str"><b>{selectedBreakdown.stringResourceCount}</b> string resources</span>
                      ),
                      ...selectedBreakdown.other.map(([k, v]) => (
                        <span key={k}><b>{v}</b> {k}</span>
                      )),
                    ].filter(Boolean).reduce<React.ReactNode[]>((acc, node, i) => (i === 0 ? [node] : [...acc, ', ', node]), [])}
                    {' '}(<b>{selectedCount}</b> total)
                  </Typography>
                )}
              </Box>

              <StudioButton
                variant="primary"
                onClick={handleConfirmOrDeploy}
                disabled={selectedCount === 0 || isDeploying || isComparing || !effectivePolicy.allowed}
                startIcon={isDeploying ? <CircularProgress size={16} /> : undefined}
              >
                {deploymentAction === 'CREATE_PR'
                  ? `Create PR for (${selectedCount}) items`
                  : `Push (${selectedCount}) items`}
              </StudioButton>
            </Box>

            {!effectivePolicy.allowed && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {effectivePolicy.reason}
              </Alert>
            )}

            <Dialog
              open={openEnvConfigsAttentionModal}
              onClose={() => setOpenEnvConfigsAttentionModal(false)}
              maxWidth="sm"
              fullWidth
              PaperProps={{ sx: { borderRadius: 2 } }}
            >
              <DialogTitle sx={{ fontWeight: 700 }}>
                Environment Configs
              </DialogTitle>
              <DialogContent>
                <Typography variant="body1">
                  We have observed <strong>
                    {(() => {
                      const envConfigsRows = sections['environmentConfigs'] ?? [];
                      const diffCount = envConfigsRows.filter((r) => r.status !== 'unchanged').length;
                      return onlyDifferences ? diffCount : envConfigsRows.length;
                    })()}
                  </strong> Environment Config(s) that require attention. Please review once.
                </Typography>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
                <StudioButton variant="primary" onClick={() => setOpenEnvConfigsAttentionModal(false)}>
                  Got it
                </StudioButton>
              </DialogActions>
            </Dialog>

            {shellBundle?.shellBundleUrl && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Shell bundle ready:{' '}
                {(() => {
                  const base = String(process.env.REACT_APP_TOOL_API_URL || '').replace(/\/+$/, '');
                  const rel = String(shellBundle.shellBundleUrl || '');
                  const abs = rel.startsWith('http') ? rel : `${base}${rel.startsWith('/') ? '' : '/'}${rel}`;
                  return (
                    <a href={abs} target="_blank" rel="noreferrer">
                      Download script
                    </a>
                  );
                })()}
              </Alert>
            )}

            {syncId && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Alert severity="info" sx={{ mb: 0, flex: '1 1 auto' }}>
                    Push in progress. syncId: <b>{syncId}</b>
                  </Alert>
                  <StudioButton
                    variant="outline"
                    size="sm"
                    onClick={refreshSyncStatus}
                    disabled={isLoadingSyncStatus}
                    startIcon={isLoadingSyncStatus ? <CircularProgress size={14} /> : undefined}
                  >
                    Refresh status
                  </StudioButton>
                </Box>
                {syncStatus?.progress?.total ? (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {syncStatus?.status || 'processing'} — {syncStatus?.progress?.completed ?? 0}/{syncStatus?.progress?.total ?? 0}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={
                        syncStatus?.progress?.total
                          ? Math.min(100, Math.round(((syncStatus?.progress?.completed ?? 0) / syncStatus.progress.total) * 100))
                          : 0
                      }
                    />
                  </Box>
                ) : null}
              </Box>
            )}
          </CardContent>
        </Card>
        </>
      )}

      {viewMode === 'history' && (
        <PromotionHistory
          onBackToSelection={() => setViewMode('selection')}
          selectedPilot={selectedPilot}
          targetEnv={targetEnv ?? null}
          compareTargetEnvironments={compareTargetEnvironments}
          rcRef={rcRef}
        />
      )}
    </Box>
  );
};

